import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Box, Button, Checkbox, Grid, IconButton } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { HourlyAccountUserGroupZonePromoCode } from '../../types';
import { datagridFilterPanelAttributes, dayjs } from '../../utils';
import { ZonePromoCodeAutocomplete } from '../inputs';
import { DatagridLinearProgress } from '../loading';

export interface PortalValidationsTableProps {
	facilityId: string;
	zonePromoCodes?: HourlyAccountUserGroupZonePromoCode[];
	parentZonePromoCodes?: HourlyAccountUserGroupZonePromoCode[];
	canEdit: boolean;
	canAdd?: boolean;
	onChange: (validations: HourlyAccountUserGroupZonePromoCode[]) => void;
}

export const PortalValidationsTable = ({
	facilityId,
	zonePromoCodes,
	parentZonePromoCodes,
	canEdit,
	canAdd = true,
	onChange,
}: PortalValidationsTableProps) => {
	const zonePromoCodeMap = useMemo(
		() => Object.fromEntries(zonePromoCodes.map((z) => [z.zonePromoCode?.id, z])),
		[zonePromoCodes],
	);

	const mergedZonePromoCodes = useMemo(
		() =>
			parentZonePromoCodes?.map((parentZonePromoCode) => {
				const inputZonePromoCode = zonePromoCodeMap[parentZonePromoCode.zonePromoCode.id];
				return {
					...parentZonePromoCode,
					id: inputZonePromoCode?.id ?? uuidv4(),
					parentId: parentZonePromoCode.id,
					parentQrValidation: parentZonePromoCode.qrValidation,
					parentWebValidation: parentZonePromoCode.webValidation,
					qrValidation: inputZonePromoCode?.qrValidation ?? false,
					webValidation: inputZonePromoCode?.webValidation ?? false,
				};
			}) ?? [],
		[zonePromoCodeMap, parentZonePromoCodes],
	);

	const renderedZonePromoCodes = parentZonePromoCodes ? mergedZonePromoCodes : zonePromoCodes;

	const handleAddRow = useCallback(() => {
		onChange([
			...zonePromoCodes,
			{
				id: uuidv4(),
				webValidation: false,
				qrValidation: true,
				zonePromoCode: null,
			},
		]);
	}, [zonePromoCodes, onChange]);

	const selectedZonePromoCodeIds = useMemo(
		() => zonePromoCodes.map((zonePromoCode) => String(zonePromoCode.zonePromoCode?.id)),
		[zonePromoCodes],
	);

	const handleDeleteRow = useCallback(
		(params) => {
			onChange(zonePromoCodes.filter((row) => row.id !== params.row.id));
		},
		[zonePromoCodes, onChange],
	);

	const handleEditRow = useCallback(
		(params, modification) => {
			onChange(
				renderedZonePromoCodes.map((row) => {
					if (params.row.id === row.id) {
						return {
							...row,
							...modification,
						};
					}
					return row;
				}),
			);
		},
		[renderedZonePromoCodes, onChange],
	);

	const columns: any[] = [
		{
			field: 'name',
			headerName: 'Validation',
			flex: 1,
			renderCell: (params) =>
				canEdit ? (
					<ZonePromoCodeAutocomplete
						required
						error={null}
						disabled={!canEdit || !!params.row.parentId}
						facilityId={facilityId}
						zonePromoCodeId={params.row.zonePromoCode?.id}
						zonePromoCodeText={params.row.zonePromoCode?.promoCode?.text}
						zoneName={params.row.zonePromoCode?.facilityZone?.name}
						rateName={params.row.zonePromoCode?.promoCode?.rate?.name}
						onChange={(zonePromoCode) => {
							let modification = { zonePromoCode: null };
							if (zonePromoCode) {
								modification = {
									zonePromoCode: {
										id: parseInt(zonePromoCode.value, 10),
										facilityZone: { name: zonePromoCode.zoneName },
										promoCode: {
											text: zonePromoCode.text,
											rate: {
												name: zonePromoCode.rateName,
											},
											startsOn: zonePromoCode.startsOn,
											expiresOn: zonePromoCode.expiresOn,
										},
									},
								};
							}
							handleEditRow(params, modification);
						}}
						ignoredOptions={selectedZonePromoCodeIds}
						size='small'
					/>
				) : (
					params.row.zonePromoCode?.promoCode?.text
				),
		},
		{
			field: 'rateName',
			headerName: 'Rate',
			width: 350,
			editable: false,
			renderCell: (params) => params.row.zonePromoCode?.promoCode?.rate?.name,
		},
		{
			field: 'zone',
			headerName: 'Zone',
			width: 250,
			editable: false,
			renderCell: (params) => params.row.zonePromoCode?.facilityZone?.name,
		},
		{
			field: 'startsOn',
			headerName: 'Starts On',
			width: 125,
			editable: false,
			renderCell: (params) => {
				const startsOn = params.row.zonePromoCode?.promoCode?.startsOn;
				return startsOn ? dayjs.utc(startsOn).format('M/D/YY') : '-';
			},
		},
		{
			field: 'expiresOn',
			headerName: 'Expires On',
			width: 125,
			editable: false,
			renderCell: (params) => {
				const expiresOn = params.row.zonePromoCode?.promoCode?.expiresOn;
				return expiresOn ? dayjs.utc(expiresOn).format('M/D/YY') : '-';
			},
		},
		{
			field: 'qrValidation',
			headerName: 'QR Code',
			width: 80,
			align: 'center',
			renderCell: (params) => (
				<Checkbox
					disabled={!canEdit || (params.row.parentId && !params.row.parentQrValidation)}
					checked={params.row.qrValidation}
					onChange={(event) => handleEditRow(params, { qrValidation: event.target.checked })}
				/>
			),
		},
		{
			field: 'webValidation',
			headerName: 'Web',
			width: 50,
			align: 'center',
			renderCell: (params) => (
				<Checkbox
					disabled={!canEdit || (params.row.parentId && !params.row.parentWebValidation)}
					checked={params.row.webValidation}
					onChange={(event) => handleEditRow(params, { webValidation: event.target.checked })}
				/>
			),
		},
		...(canAdd && canEdit
			? [
					{
						field: ' ',
						headerName: '',
						align: 'right',
						width: 40,
						renderCell: (params) => (
							<IconButton onClick={() => handleDeleteRow(params)}>
								<DeleteIcon />
							</IconButton>
						),
					},
				]
			: []),
	];

	return (
		<>
			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				<h4>Validations</h4>
				{canAdd && (
					<Grid container sx={{ marginTop: '5px', justifyContent: 'end' }}>
						<Grid item>
							<Button size='small' onClick={handleAddRow}>
								<AddIcon /> Add Validation
							</Button>
						</Grid>
					</Grid>
				)}
			</Box>
			<DataGridPro
				hideFooter
				sx={{ height: 'auto', minHeight: '92px', mb: canAdd ? 0 : 3 }}
				editMode='row'
				rows={renderedZonePromoCodes ?? []}
				rowHeight={45}
				onCellEditStop={handleEditRow}
				columnHeaderHeight={45}
				columns={columns}
				slots={{
					loadingOverlay: DatagridLinearProgress,
				}}
				slotProps={{
					filterPanel: {
						sx: {
							'& .MuiDataGrid-filterFormValueInput': { width: 240 },
						},
						...datagridFilterPanelAttributes,
					},
				}}
			/>
		</>
	);
};
